import {
  Component,
  HostListener,
  ElementRef,
  ViewChild,
  OnInit,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "angular2-cookie/core";
import { ActivatedRoute } from "@angular/router";
import { WidgetService } from "./widget.service";
import {
  AppointmentType,
  CommunicationChannel,
  Doctor,
  PersonData,
  PotentialAppointmentListModel,
  PotentialAppointmentSlot,
  QueryParams,
} from "./widget.model";
import * as moment from "moment";
import { BsModalService } from "ngx-bootstrap/modal";
import { BsModalRef } from "ngx-bootstrap/modal/bs-modal-ref.service";
import { StepOneComponent } from "./step-one/step-one.component";
import { environment } from "../environments/environment";

interface Font {
  name: string;
  url: string;
  format: string;
}

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
})
export class AppComponent {
  appointmentTypeList: AppointmentType[];
  preselectedAppointmentType: AppointmentType = null;
  potentialAppointmentList: PotentialAppointmentListModel[];

  color2: string = environment.color2;
  color2_80: string = environment.color2_80;
  color4: string = environment.color4;
  color7: string = environment.color7;
  font: Font = environment.font;
  cookie: string = environment.cookie;
  shouldShowResources: boolean = environment.shouldShowResources;

  dedicatedTel: string = environment.telephone;
  dedicatedEmail: string = environment.email;
  isIroc: boolean =
    environment.websiteName === "IROC" ||
    environment.websiteName === "IROC Kontaktlinsen";

  params: QueryParams = {
    widgetUid: environment.widgetUid,
    languageIsoCode: null,
    dates: [],
    appointmentTypeUid: null,
    doctorId: null,
  };
  selectedDate: Date;
  selectedDateFromDatepicker: Date;
  selectedSlot: any;
  stepTwo: boolean;
  stepThree: boolean;
  workdays: Array<string> = [];
  errorPatientExist: boolean;
  errorNotExist: boolean;
  bookingFailed: boolean;
  shouldResetForm: boolean;
  doctorList: Doctor[];

  // TODO: Check CH - DE conflict within selected language iso code
  selectedLanguageIsoCode: string = "CH";

  @ViewChild("modal") modal: ElementRef;
  @ViewChild(StepOneComponent) stepOne: StepOneComponent;

  modalRef: BsModalRef;
  config = {
    ignoreBackdropClick: true,
  };

  @HostListener("window:resize")
  onResize() {
    if (this.selectedDate && this.params.appointmentTypeUid) {
      if (window.innerWidth > 768) {
        if (this.params.dates.length === 1) {
          this.params.dates = this.getWorkdaysFromSelectedDate(
            this.selectedDate
          );
          this.stepTwo = false;
          this.potentialAppointmentList = [];
          this.getAppointmentSlots();
        }
      } else {
        if (this.params.dates.length !== 1) {
          this.params.dates = [moment(this.selectedDate).format("YYYY-MM-DD")];
          this.stepTwo = false;
          this.potentialAppointmentList = [];
          this.getAppointmentSlots();
        }
      }
    }
  }

  constructor(
    private translate: TranslateService,
    private cookieService: CookieService,
    private route: ActivatedRoute,
    private widgetService: WidgetService,
    private translateService: TranslateService,
    private modalService: BsModalService
  ) {
    translate.setDefaultLang("en");
    document.documentElement.style.setProperty("--color2", this.color2);
    document.documentElement.style.setProperty("--color4", this.color4);
    document.documentElement.style.setProperty("--color2_80", this.color2_80);
    document.documentElement.style.setProperty("--color7", this.color7);
    document.documentElement.style.setProperty("--fontName", this.font.name);

    if (this.isIroc) {
      document.body.classList.add("iroc");
    }
    if (this.cookie !== "") {
      this.cookieService.put(
        "bop-v4-st-widget",
        "A81915A6B20D65DA0CC786C5094FB45F"
      );
    }
    this.route.queryParams.subscribe((params) => {
      if (params["langIsoCode"]) {
        this.params.languageIsoCode = params["langIsoCode"];
        this.selectedLanguageIsoCode = params["langIsoCode"].toUpperCase();
      } else {
        this.params.languageIsoCode = "de";
      }
      if (this.params.widgetUid && this.params.languageIsoCode) {
        this.translate.use(this.params.languageIsoCode);
        let queryParams = {
          widgetUid: this.params.widgetUid,
          languageIsoCode: this.params.languageIsoCode,
        };
        this.widgetService
          .getAppointmentTypeList(queryParams)
          .subscribe((list) => {
            this.appointmentTypeList = list;
            this.appointmentTypeList.forEach((item: AppointmentType) => {
              item.name = `${item.name} ${
                item.description ? `| ${item.description}` : ""
              }`;
            });
            if (
              this.appointmentTypeList &&
              this.appointmentTypeList.length === 1
            ) {
              this.preselectedAppointmentType = this.appointmentTypeList[0];

              this.onSelectedAppointmentTypeUid(
                this.preselectedAppointmentType.uid
              );
            }
            this.widgetService
              .getConsultationHours(queryParams)
              .subscribe((res) => {
                let days: Array<string> = [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ];
                let startDay = res[0];
                let endDay = res[res.length - 1];
                for (let i = startDay; i <= endDay; i++) {
                  this.workdays.push(days[i]);
                }
              });
          });
      }
    });
  }

  onSelectedAppointmentTypeUid(typeUid: string) {
    if (!this.shouldShowResources) return;
    this.widgetService.getDoctorList(typeUid, this.params).subscribe((list) => {
      this.doctorList = list.filter((item: Doctor) => item.isDoctor);
      if (this.doctorList && this.doctorList.length) {
        let item = {
          doctorId: null,
          name: this.translateService.instant("all"),
          isDoctor: true,
        };
        this.doctorList.unshift(item);
      }
    });
  }

  selectDateFromDatepicker(date: Date) {
    this.selectedDateFromDatepicker = date;
  }

  bookAppointment(personData: PersonData) {
    this.shouldResetForm = false;
    let data = {
      potentialAppointment: this.selectedSlot,
      patient: {
        personInfo: personData,
      },
    };
    let queryParams = {
      widgetUid: this.params.widgetUid,
      languageIsoCode: this.params.languageIsoCode,
    };
    this.errorPatientExist = false;
    this.errorNotExist = false;
    this.widgetService.validatePatient(data, queryParams).subscribe((res) => {
      if (!res.isSuccess) {
        if (res.payLoad === 10004) {
          this.errorPatientExist = true;
        } else if (res.payLoad === 10002) {
          this.errorNotExist = true;
        }
      } else {
        setTimeout(() => {
          this.modalRef = this.modalService.show(this.modal, this.config);
        }, 0);
        this.sendToParent(personData);
        this.stepTwo = false;
        this.stepThree = false;
        this.potentialAppointmentList = [];
        this.selectedSlot = null;
        this.widgetService
          .bookAppointment(data, queryParams)
          .subscribe((res) => {
            if (!res || !res.isSuccess) {
              this.selectedDateFromDatepicker = null;
              this.selectedDate = null;
              this.params.doctorId = null;
              this.params.appointmentTypeUid = null;
              this.bookingFailed = true;
              this.shouldResetForm = true;
              setTimeout(() => {
                this.bookingFailed = false;
              }, 4000);
            }
            return;
          });
      }
    });
  }

  getAppointmentSlots() {
    this.selectedSlot = null;
    this.stepThree = false;
    this.widgetService
      .getPotentialAppointments(this.params)
      .subscribe((res) => {
        res.forEach((value) => {
          value.weekDay = moment(value.date).format("dddd");
        });
        this.potentialAppointmentList = res;
        this.stepTwo =
          this.potentialAppointmentList &&
          this.potentialAppointmentList.length > 0;
      });
  }

  getPotentialAppointments(params: any) {
    this.params.doctorId = params.doctorId;
    this.params.appointmentTypeUid = params.appointmentTypeUid;
    if (window.innerWidth > 768) {
      this.params.dates = this.getWorkdaysFromSelectedDate(params.date);
    } else {
      this.params.dates = [moment(params.date).format("YYYY-MM-DD")];
    }
    this.selectedDate = params.date;
    this.getAppointmentSlots();
  }

  getWorkdaysFromSelectedDate(date: Date) {
    let startOfWeek = moment(date).startOf("isoWeek");
    let endOfWeek = moment(date).endOf("isoWeek");

    let days = [];
    let day = startOfWeek;

    while (day <= endOfWeek) {
      let dayName = moment(day).format("dddd");
      if (this.workdays.indexOf(dayName) !== -1) {
        days.push(moment(day).format("YYYY-MM-DD"));
      }
      day = day.clone().add(1, "d");
    }

    return days;
  }

  getMorePotentialAppointments(str: string) {
    if (str === "previous") {
      if (window.innerWidth > 768) {
        this.selectedDate = moment(this.selectedDate).subtract(7, "d").toDate();
        this.params.dates = this.getWorkdaysFromSelectedDate(this.selectedDate);
      } else {
        this.selectedDate = moment(this.selectedDate).subtract(1, "d").toDate();
        this.params.dates = [moment(this.selectedDate).format("YYYY-MM-DD")];
      }
    } else if (str === "next") {
      if (window.innerWidth > 768) {
        this.selectedDate = moment(this.selectedDate).add(7, "d").toDate();
        this.params.dates = this.getWorkdaysFromSelectedDate(this.selectedDate);
      } else {
        this.selectedDate = moment(this.selectedDate).add(1, "d").toDate();
        this.params.dates = [moment(this.selectedDate).format("YYYY-MM-DD")];
      }
    }
    this.getAppointmentSlots();
  }

  selectSlot(slot: PotentialAppointmentSlot) {
    this.selectedSlot = slot;
    this.stepThree = true;
  }

  sendToParent(personData: PersonData) {
    const email = personData.communicationChannels.find(
      (x) => x.typeId === "c41eddc2-827e-47de-9502-06ad7cfcea4e"
    );
    let data = "";
    if (email) {
      data = email.content;
    }
    const type = personData.type;

    if (type == "login") {
      parent.postMessage(
        `activateClickAnalyticsLogin_${data}`,
        parent.location.origin
      );
    } else {
      parent.postMessage(
        `activateClickAnalyticsRegister_${data}`,
        parent.location.origin
      );
    }
  }

  //close confirm modal
  closeModal() {
    this.modalRef.hide();

    this.shouldResetForm = true;
    this.stepTwo = false;
    this.stepThree = false;
    this.selectedDate = null;
    this.selectedDateFromDatepicker = null;
    this.selectedSlot = null;
    this.stepOne.resetForm();
  }
}



// WEBPACK FOOTER //
// C:/Users/strahinja.aparac/Desktop/git/source/healthcare/v2/app/appointment-widget/src/app/app.component.ts